/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';
import { CmsImage } from '@hyphengroup/cms-components/components/CmsImage';
import classNames from 'classnames';
import React from 'react';
import {
  ComponentGridModule,
  EnumGridModuleAlign,
  EnumGridModuleBackgroundColor,
  EnumGridModuleWidthMode,
} from '$/(cms)/_modules/cmsService/types';
import { PageContentContainerType } from '$/components/PageContentContainer';

const bgColorClassFromGridModuleBackgroundColor: Record<
  EnumGridModuleBackgroundColor,
  string
> = {
  [EnumGridModuleBackgroundColor.PrimaryLight]: classNames(
    'bg-fill/primary-light',
  ),
  [EnumGridModuleBackgroundColor.Alternative_1]: classNames(
    'bg-fill/alternative-1',
  ),
  [EnumGridModuleBackgroundColor.Alternative_2]: classNames(
    'bg-fill/alternative-2',
  ),
  [EnumGridModuleBackgroundColor.Alternative_3]: classNames(
    'bg-fill/alternative-3',
  ),
  [EnumGridModuleBackgroundColor.SecondaryLight]: classNames(
    'bg-fill/secondary-light',
  ),
};

export const FeaturesGridModule: React.FC<
  ComponentGridModule & {
    ContentContainer?: PageContentContainerType;
  }
> = ({
  align,
  description,
  headline,
  widthMode,
  bgColor,
  item,
  ContentContainer = (p) => <div {...p} />,
}) => {
  const bgColorClass = bgColor
    ? bgColorClassFromGridModuleBackgroundColor[bgColor]
    : undefined;
  const isFullWidth = widthMode === EnumGridModuleWidthMode.FullWidth;
  const isCenterAlignment = align === EnumGridModuleAlign.Center;

  return (
    <section
      className={classNames(
        'flex justify-center py-spacing/padding/section-y',
        bgColorClass,
      )}
    >
      <ContentContainer>
        <div
          className={classNames('flex flex-col gap-spacing/gap/y', {
            'md:w-general/narrow-content-max-width-1 md:max-w-general/narrow-content-max-width-1':
              !isFullWidth,
            'w-full': isFullWidth,
          })}
        >
          {headline && (
            <div
              className={classNames(
                'mhg-alpha-typography-display-1 text-text/primary',
                {
                  'text-center': isCenterAlignment,
                },
              )}
            >
              {headline}
            </div>
          )}
          {description && (
            <div
              className={classNames(
                'mhg-alpha-typography-body-1-relaxed text-text/secondary [&_a]:underline [&_a]:text-[--text-link]',
                {
                  'text-center': isCenterAlignment,
                },
              )}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: description ?? '' }}
            />
          )}
          {item?.length && (
            <div
              className={classNames(
                'grid w-full grid-cols-1 gap-spacing/gap/x md:grid-cols-2',
                {
                  'lg:grid-cols-3': isFullWidth && item.length % 3 === 0,
                },
              )}
            >
              {item?.map((i, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={classNames(
                    'flex gap-spacing/gap/y rounded-radius/card border border-borders/default px-spacing/padding/x-sm py-spacing/padding/y bg-fill/card',
                  )}
                >
                  {i.image?.url && (
                    <CmsImage
                      sizes={{ _: 96 }}
                      src={i.image.url}
                      alt="Feature image"
                      height={96}
                      width={96}
                      className="size-shapes/pictogram-lg"
                    />
                  )}
                  <div className="flex w-full flex-col gap-spacing/gap/soft-8px">
                    <div className="mhg-alpha-typography-title-2">
                      {i.title}
                    </div>
                    {i.subtitle && (
                      <div
                        className="mhg-alpha-typography-body-1-relaxed [&_a]:underline [&_a]:text-[--text-link]"
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{ __html: i.subtitle }}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </ContentContainer>
    </section>
  );
};
